import { RightSidePanel } from '@/common/Sidepanel';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useEffect, useState } from 'react';
import { DocumentViewerContainer } from './document';
import { ChatHistoryContainer } from './history';
import { ChatMessageUINew } from './messages';

/**
 * ChatMainLayout component
 *
 * This component is the main layout for the chat application.
 * It contains the chat history panel on the right side and the main chat messages area.
 * The chat history panel can be toggled open and closed, and it displays the chat history.
 */
function ChatMainLayout(): React.ReactNode {
  const [chatPanelOpen, setChatPanelOpen] = useState<boolean>(true);
  const [chatHistoryLoading, setChatHistoryLoading] = useState<boolean>(false);
  const [currentChatID, setCurrentChatID] = useState<string>('');
  const [viewMode, setViewMode] = useState<'history' | 'document'>('history');

  // Get document viewer state from the store
  const documentViewer = useAlchemystStoreForAi(
    (store) => store.documentViewer
  );

  // Set currentChatID from query param if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chatId = params.get('chatId');
    if (chatId) {
      setCurrentChatID(chatId);
    }
  }, []);

  // Automatically switch to document view when fileUrl is available
  useEffect(() => {
    if (
      documentViewer.isOpen &&
      (documentViewer.fileUrls.length > 0 || documentViewer.scratchpadContent)
    ) {
      setViewMode('document');
      setChatPanelOpen(true); // Make sure the panel is open
    }
  }, [
    documentViewer.isOpen,
    documentViewer.fileUrls,
    documentViewer.scratchpadContent,
  ]);

  // Different widths for different views
  const historyPanelWidth = '20vw';
  const documentPanelWidth = '40vw';

  // Get current panel width based on view mode
  const currentPanelWidth =
    viewMode === 'history' ? historyPanelWidth : documentPanelWidth;

  return (
    <Box>
      {/* Floating Icons */}
      <Box
        sx={{
          position: 'fixed',
          right: chatPanelOpen ? currentPanelWidth : 0,
          top: '1vh', // Position at top of panel
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          transition: 'right 0.3s ease-in-out',
        }}
      >
        <IconButton
          onClick={() => setViewMode('history')}
          sx={{
            backgroundColor: viewMode === 'history' ? '#EAEAEA' : '#EAEAEA',
            color: 'black',
            '&:hover': {
              backgroundColor: viewMode === 'history' ? '#EAEAEA' : '#EAEAEA',
            },
            boxShadow: 3,
            width: '40px',
            height: '40px',
          }}
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => setViewMode('document')}
          sx={{
            backgroundColor: viewMode === 'document' ? '#EAEAEA' : '#EAEAEA',
            color: 'black',
            '&:hover': {
              backgroundColor: viewMode === 'document' ? '#EAEAEA' : '#EAEAEA',
            },
            boxShadow: 3,
            width: '40px',
            height: '40px',
          }}
        >
          <DescriptionIcon fontSize="small" />
        </IconButton>
      </Box>

      <RightSidePanel
        open={chatPanelOpen}
        setOpen={setChatPanelOpen}
        panelWidth={currentPanelWidth}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            transition: 'width 0.3s ease-in-out',
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {viewMode === 'history' ? (
              <ChatHistoryContainer
                currentChatID={currentChatID}
                setCurrentChatID={setCurrentChatID}
                loading={chatHistoryLoading}
              />
            ) : (
              <DocumentViewerContainer />
            )}
          </Box>
        </Box>
      </RightSidePanel>

      <Box
        marginRight={chatPanelOpen ? currentPanelWidth : 0}
        maxWidth={chatPanelOpen ? `calc(100% - ${currentPanelWidth})` : '100%'}
        sx={{ transition: 'margin-right 0.3s, max-width 0.3s' }}
      >
        <ChatMessageUINew
          currentChatID={currentChatID}
          setCurrentChatID={setCurrentChatID}
        />
      </Box>
    </Box>
  );
}

export default ChatMainLayout;
